export const getObserverOptions = () => ({
	root: null,
	threshold: 0.01,
	// 优化：向下滚动优先（上方 100px，下方 300px）
	rootMargin: "100px 0px 300px 0px"
});

// 检查是否已包含图片处理参数
export const isLowQualityImage = (url: string): boolean => {
	return /imageMogr2|imageView2/.test(url);
};

// 生成低质量图片URL
export const getLowQualityImageUrl = (originalSrc: string): string => {
	// 如果已经包含图片处理参数，直接返回，避免重复拼接
	if (isLowQualityImage(originalSrc)) {
		return originalSrc;
	}

	return `${originalSrc}?imageMogr2/format/webp/quality/20/ignore-error/1`;
};

const URL_REGEX = /url\(['"]?([^'"()]+)['"]?\)/;

// 从 CSS background-image 字符串中提取 URL
const extractUrlFromCssBackground = (backgroundImage: string): string => {
	if (!backgroundImage || backgroundImage === "none") return "";
	const urlMatch = backgroundImage.match(URL_REGEX);
	return urlMatch ? urlMatch[1] : "";
};

// 获取元素的背景图片 URL（优先内联样式，回退到计算样式）
export const getBackgroundImageUrl = (el: HTMLElement): string => {
	const inlineUrl = extractUrlFromCssBackground(el.style.backgroundImage);
	if (isValidImageUrl(inlineUrl)) return inlineUrl;

	const computedUrl = extractUrlFromCssBackground(window.getComputedStyle(el).backgroundImage);
	if (isValidImageUrl(computedUrl)) return computedUrl;

	return "";
};

// 通用的高质量图片加载处理函数
export const createHighQualityLoader = (originalSrc: string, onSuccess: () => void, onError: () => void) => {
	const img = new Image();

	const handleLoad = () => {
		onSuccess();
	};

	const handleError = (e: Event) => {
		e.stopPropagation();
		e.preventDefault();
		onError();
	};

	img.addEventListener("load", handleLoad, { once: true });
	img.addEventListener("error", handleError, { once: true, capture: true });
	img.src = originalSrc;

	return () => {
		img.removeEventListener("load", handleLoad);
		img.removeEventListener("error", handleError);
		img.src = "";
	};
};

// 比较两个 URL 是否相同
export const isSameSrc = (src1: string, src2: string): boolean => {
	if (src1 === src2) return true;

	// 移除查询参数后比较
	const cleanSrc1 = src1.split("?")[0];
	const cleanSrc2 = src2.split("?")[0];

	return cleanSrc1 === cleanSrc2;
};

/**
 * 验证图片 URL 是否有效
 * @param url 图片 URL
 * @returns 是否有效
 */
export const isValidImageUrl = (url: string): boolean => {
	if (!url) return false;
	const invalidValues = ["", "undefined", "null", "none", "#"];
	if (invalidValues.includes(url)) return false;

	// data URI 不需要懒加载（已经是 base64）
	if (url.startsWith("data:")) return false;

	// 支持的图片格式（更全面）
	const imageFormats = ["jpg", "jpeg", "png", "gif", "webp", "svg", "avif", "bmp", "tiff", "tif", "ico", "heic", "heif", "jxl", "apng"].join("|");

	// 必须包含图片扩展名或 http(s) 协议
	const pattern = new RegExp(`\\.(${imageFormats})|https?:\\/\\/`, "i");
	return pattern.test(url);
};
